// src/stores/profileStore.js
// The player's display name, shared by Home, Join Room, My Rooms, Settings and
// NameIt Run. Mirrors the mobile app's stores/profileStore.ts.
//
// Two copies are kept in step:
// - localStorage "nameit.displayName": this device's copy. "playerName" is
//   the name inside the current room and is cleared when a room is left, so
//   it is only read once, to carry over a name typed before this store.
// - users/{uid}.displayName: the same field mobile and the leaderboard read.
//   Only written when an account already exists; browsing never creates one.
import { create } from "zustand";
import { doc, updateDoc } from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { db } from "../firebase";
import authService from "../Services/authService";
import accountService from "../Services/accountService";
import { validateDisplayName } from "../utils/validateDisplayName";

export const DISPLAY_NAME_KEY = "nameit.displayName";


const readLocal = () => {
  try {
    return (localStorage.getItem(DISPLAY_NAME_KEY) || localStorage.getItem("playerName") || "").trim();
  } catch {
    return "";
  }
};

const writeLocal = (name) => {
  try {
    localStorage.setItem(DISPLAY_NAME_KEY, name);
  } catch {
    /* storage unavailable */
  }
};

const writeAccount = async (name) => {
  const uid = await authService.getExistingUserId();
  if (!uid) return;
  await updateDoc(doc(db, "users", uid), { displayName: name });
  // Signed-in accounts also show the Auth profile name (Settings' Account
  // row); keep it the same.
  const user = authService.auth.currentUser;
  if (user && !user.isAnonymous) await updateProfile(user, { displayName: name });
};

const useProfileStore = create((set, get) => ({
  displayName: readLocal(),
  loaded: false,

  // The account's name wins over this browser's copy, so a rename on mobile
  // shows here too. A guest without one keeps the local name. Reads the
  // account once per session; pass { force: true } after sign-in changes.
  loadDisplayName: async ({ force = false } = {}) => {
    if (get().loaded && !force) return;
    set({ loaded: true });
    try {
      const name = (await accountService.getUserDisplayName())?.trim();
      if (name && name !== get().displayName) {
        writeLocal(name);
        set({ displayName: name });
      }
    } catch (error) {
      console.warn("[profileStore] failed to load display name:", error);
    }
  },

  /** Signing out: the next person on this device starts without a name
   *  rather than inheriting this one. */
  clearDisplayName: () => {
    try {
      localStorage.removeItem(DISPLAY_NAME_KEY);
      localStorage.removeItem("playerName");
    } catch {
      /* storage unavailable */
    }
    set({ displayName: "", loaded: false });
  },

  /** Validates and saves. Throws with a player-facing message on failure. */
  saveDisplayName: async (raw) => {
    const validation = validateDisplayName(raw);
    if (!validation.isValid) {
      throw new Error(validation.message || "Please choose a different name");
    }
    const name = raw.trim();
    writeLocal(name);
    set({ displayName: name });
    try {
      await writeAccount(name);
    } catch (error) {
      // The name is already saved on this device; the account copy catches
      // up on the next save or room.
      console.warn("[profileStore] failed to save display name to account:", error);
    }
  },

  // Called after a room is created or joined, when a guest account is sure to
  // exist: copies a locally-typed name onto the account if it has none yet.
  syncToAccount: async () => {
    const name = get().displayName;
    if (!name) return;
    try {
      const existing = await accountService.getUserDisplayName({ savedOnly: true });
      if (!existing) await writeAccount(name);
    } catch (error) {
      console.warn("[profileStore] failed to sync display name:", error);
    }
  },
}));

export default useProfileStore;
